// TODO: MVP-3284 Rename folder to NCCard and delete original
import complianceCbdSrc from '@assets/images/compliance-cbd.png';
import complianceCdpSrc from '@assets/images/compliance-cdp.png';
import complianceCsrd from '@assets/images/compliance-csrd.png';
import complianceEudrSrc from '@assets/images/compliance-eudr.png';
import complianceGbfSrc from '@assets/images/compliance-gbf.jpeg';
import complianceGreenhouseSrc from '@assets/images/compliance-greenhouse.png';
import complianceGriSrc from '@assets/images/compliance-gri.png';
import complianceIucnSrc from '@assets/images/compliance-iucn.png';
import complianceNpiSrc from '@assets/images/compliance-npi.png';
import complianceRainforestSrc from '@assets/images/compliance-rainforest.png';
import complianceSaiSrc from '@assets/images/compliance-sai.png';
import complianceSbtiSrc from '@assets/images/compliance-sbti.png';
import complianceSbtnSrc from '@assets/images/compliance-sbtn.png';
import complianceSdgSrc from '@assets/images/compliance-sdg.png';
import complianceTnfdSrc from '@assets/images/compliance-tnfd.png';
import {
  ButtonLink,
  cn,
  Divider,
  IconHalo,
  InfoPopover,
  Stack,
  StackProps,
  ToggleButton,
  ToggleButtonGroup,
  ToggleButtonGroupProps,
} from 'component-library';
import i18next from 'i18next';
import React, {
  createContext,
  FC,
  forwardRef,
  HTMLAttributes,
  PropsWithChildren,
  Suspense,
  useContext,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';

import { ConservationBenchmarkType, FactLike, TrendLabel } from '@/api/rest/resources/conservation';
import { ComplianceFrameworks } from '@/api/rest/resources/types/organization';
import { BenchmarkPill, TrendPill } from '@/components/Pill/ConservationPill';
import { ComplianceFrameworkLogo } from '@/config/constants';
import { useIsConservation } from '@/hooks/useIsConservation';
import { usePathSet } from '@/hooks/usePathSet';
import { useScreenSize } from '@/hooks/useScreenSize';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { buildPath } from '@/utils/buildPath';
import { exhaustivenessCheck } from '@/utils/exhaustivenessCheck';
import { DateLike, getLatestDate } from '@/utils/formatting/date';

export type NCCardAnalysisType = 'total' | 'average';

type NCCardContextValue = {
  analysisType: NCCardAnalysisType;
  setAnalysisType: (anaylsisType: NCCardAnalysisType) => void;
};

export const NCCardContext = createContext<NCCardContextValue>({
  analysisType: 'total',
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setAnalysisType: () => {},
});

export const useNCCardContext = () => useContext(NCCardContext);

const ExplainerContext = createContext<[HTMLDivElement | null, (element: HTMLDivElement | null) => void]>([
  null,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  () => {},
]);

const ExplainerPortal = ({ children }: PropsWithChildren) => {
  const [explainer] = React.useContext(ExplainerContext);

  if (!explainer || !children) return null;

  return createPortal(<Suspense fallback={null}>{children}</Suspense>, explainer);
};

const LargeScreenExplainerOutlet = () => {
  const [, setExplainer] = useContext(ExplainerContext);
  const isSmallScreen = useScreenSize() === 'small';

  if (isSmallScreen) return null;

  return <div ref={(ref) => setExplainer(ref)} />;
};

export const SmallScreenExplainerOutlet = () => {
  const [, setExplainer] = useContext(ExplainerContext);
  const isSmallScreen = useScreenSize() === 'small';

  if (!isSmallScreen) return null;

  return <Stack className='px-6 pb-6' ref={(ref) => setExplainer(ref)} />;
};

export type NCCardProps = HTMLAttributes<HTMLDivElement> & {
  explainer?: React.ReactNode;
};

export const NCCard = forwardRef<HTMLDivElement, NCCardProps>(
  ({ className, explainer, children, ...delegated }, ref) => {
    const isConservation = useIsConservation();

    const [analysisType, setAnalysisType] = useState<NCCardContextValue['analysisType']>('total');
    const explainerState = React.useState<HTMLDivElement | null>(null);

    return (
      <NCCardContext.Provider value={{ analysisType, setAnalysisType }}>
        <ExplainerContext.Provider value={explainerState}>
          <div
            ref={ref}
            className={cn(
              'rounded-2xl bg-white-100',
              {
                'border border-neutral-black-12': !isConservation,
              },
              className,
            )}
            {...delegated}
          >
            {children}
          </div>
          {explainer && <ExplainerPortal>{explainer}</ExplainerPortal>}
        </ExplainerContext.Provider>
      </NCCardContext.Provider>
    );
  },
);
NCCard.displayName = 'NCCard';

export type NCCardHeaderProps = Omit<StackProps, 'icon' | 'title'> & {
  icon?: React.ReactNode;
  title?: React.ReactNode;
};

export const NCCardHeader: FC<NCCardHeaderProps> = forwardRef<HTMLDivElement, NCCardHeaderProps>(
  ({ title, icon, children, className, ...delegated }, ref) => {
    const isLargeScreen = useScreenSize() === 'large';

    return (
      <div ref={ref} className='full-bleed-x'>
        <Stack
          direction='row'
          spacing={4}
          centerMain
          className={cn('lg:grid lg:grid-cols-[1fr_auto_1fr] justify-between p-6', className)}
          {...delegated}
        >
          <Stack direction='row' spacing={4} className={cn('items-center', children ? '' : 'flex-1')}>
            {!!icon && <IconHalo>{icon}</IconHalo>}
            <span className='typography-h3'>{title}</span>
          </Stack>
          {isLargeScreen && children}
          <Stack direction='row' centerMain spacing={8} className='justify-end'>
            {!isLargeScreen && children}
            <LargeScreenExplainerOutlet />
          </Stack>
        </Stack>
        <Divider />
      </div>
    );
  },
);
NCCardHeader.displayName = 'NCCardHeader';

export type NCCard2SectionLayoutProps = HTMLAttributes<HTMLDivElement> & {
  showDivider?: boolean;
};

export const NCCard2SectionLayout = forwardRef<HTMLDivElement, NCCard2SectionLayoutProps>(
  ({ className, showDivider = true, ...delegated }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'sm:[&>*]:px-6',
          showDivider && 'divide-y divide-divider px-2',
          'md:grid md:w-auto',
          showDivider && 'md:grid-cols-2',
          showDivider && 'md:divide-x md:divide-y-0',
          'md:[&>*]:px-8',
          className,
        )}
        {...delegated}
      />
    );
  },
);
NCCard2SectionLayout.displayName = 'NCCard2SectionLayout';

export type NCCardSectionHeaderProps = {
  title: React.ReactNode;
  supplementaryInfo?: React.ReactNode;
  popover?: {
    title?: React.ReactNode;
    body?: React.ReactNode;
  };
};

export const NCCardSectionHeader: FC<NCCardSectionHeaderProps> = ({ title, supplementaryInfo, popover }) => {
  const isSmallScreen = useScreenSize() === 'small';

  return (
    <Stack direction='row' className='items-start justify-between gap-2'>
      <span className='typography-body1'>
        {title}
        {popover && !isSmallScreen && (
          <span className='mx-2 align-middle leading-[0.8]'>
            <InfoPopover position='right' title={popover.title} body={popover.body} />
          </span>
        )}
      </span>
      {supplementaryInfo && !isSmallScreen && (
        <span className='typography-caption mt-[3px] flex-1 whitespace-nowrap text-right text-text-secondary'>
          {supplementaryInfo}
        </span>
      )}
    </Stack>
  );
};

export type NCCardAnalysisTypeToggleProps = Omit<ToggleButtonGroupProps<NCCardAnalysisType>, 'value'> & {
  value?: NCCardAnalysisType;
};

export const NCCardAnalysisTypeToggle: FC<NCCardAnalysisTypeToggleProps> = ({
  onChange,
  value,
  className,
  ...delegated
}) => {
  const { t } = useTranslation();
  const { analysisType, setAnalysisType } = useNCCardContext();

  const handleChange: NCCardAnalysisTypeToggleProps['onChange'] = (newValue) => {
    onChange?.(newValue);
    setAnalysisType(newValue);
  };

  return (
    <ToggleButtonGroup
      size='small'
      value={value ?? analysisType}
      onChange={handleChange}
      className={className}
      {...delegated}
    >
      <ToggleButton value='total'>{t('global.ui.toggle.total')}</ToggleButton>
      <ToggleButton value='average'>{t('global.ui.toggle.perHa')}</ToggleButton>
    </ToggleButtonGroup>
  );
};

export type NCCardComplianceFooterProps = HTMLAttributes<HTMLDivElement> & {
  logos: ComplianceFrameworkLogo[];
};

export const NCCardComplianceFooter: FC<NCCardComplianceFooterProps> = ({ logos, className, ...delegated }) => {
  const { t } = useTranslation();
  const isSmallScreen = useScreenSize() === 'small';

  return (
    <>
      <SmallScreenExplainerOutlet />
      <div
        data-testid='compliance-footer'
        className={cn('flex flex-col items-center justify-between gap-3 lg:flex-row px-6 py-4', className)}
        {...delegated}
      >
        {!isSmallScreen && (
          <Stack direction='row' spacing={3} className='flex-wrap items-center'>
            <span className='typography-body1 text-text-primary'>{t('global.nca.complianceLogos.logosLabel')}</span>
            {logos.map((logo) => (
              <ComplianceLogo key={logo} logo={logo} />
            ))}
            <FrameworksLink />
          </Stack>
        )}
      </div>
    </>
  );
};

export const FrameworksLink = () => {
  const { t } = useTranslation();
  const pathSet = usePathSet();
  const isSmallScreen = useScreenSize() === 'small';
  const organizationId = useOrganizationId();

  const { pathname } = useLocation();

  const params = useParams<{ projectId: string }>();
  const { projectId } = params;

  const to = buildPath(pathSet.projectsComplianceFrameworks, {
    queryParams: projectId ? { projectId, organizationId } : undefined,
  });

  return (
    <ButtonLink
      to={to}
      state={{
        previousPath: pathname,
      }}
      variant='text'
      className='px-0 hover:bg-transparent hover:text-primary-hover underline'
      size='small'
    >
      {isSmallScreen
        ? t('global.nca.complianceLogos.smallScreenButtonLabel')
        : t('global.nca.complianceLogos.buttonLabel')}
    </ButtonLink>
  );
};

export const ComplianceLogo: FC<{ logo: string }> = ({ logo }) => {
  const getLogoDetails = (framework: ComplianceFrameworks) => {
    switch (framework) {
      case ComplianceFrameworks.eudr:
        return {
          src: complianceEudrSrc,
          alt: 'EUDR',
          label: 'EUDR',
        };
      case ComplianceFrameworks.greenhouse:
        return {
          src: complianceGreenhouseSrc,
          alt: 'Greenhouse Gas Protocol',
          label: (
            <span>
              Greenhouse <span className='whitespace-nowrap'>Gas Protocol</span>
            </span>
          ),
        };
      case ComplianceFrameworks.sai:
        return {
          src: complianceSaiSrc,
          alt: 'SAI',
          label: 'SAI Platform',
        };
      case ComplianceFrameworks.science:
        return {
          src: complianceSbtiSrc,
          alt: 'SBTi',
          label: (
            <span>
              <span className='whitespace-nowrap'>Science-Based</span> Targets
            </span>
          ),
        };
      case ComplianceFrameworks.tnfd:
        return {
          src: complianceTnfdSrc,
          alt: 'Taskforce on Nature-related Financial Disclosures',
          label: (
            <span>
              <span className='whitespace-nowrap'>TNFD</span>
            </span>
          ),
        };
      case ComplianceFrameworks.rac:
        return {
          src: complianceRainforestSrc,
          alt: 'Rainforest Alliance Certification',
          label: <span className='whitespace-nowrap'>RAINFOREST ALLIANCE CERTIFICATION</span>,
        };
      case ComplianceFrameworks.csrd:
        return {
          src: complianceCsrd,
          alt: 'Corporate Sustainability Reporting Directive',
          label: <span className='whitespace-nowrap'>CSRD (ESRS E4)</span>,
        };
      case ComplianceFrameworks.npi:
        return {
          src: complianceNpiSrc,
          alt: 'Nature Positive Initiative',
          label: <span className='whitespace-nowrap'>NPI</span>,
        };
      case ComplianceFrameworks.sbti:
      case ComplianceFrameworks.sbtn:
        return {
          src: complianceSbtnSrc,
          alt: 'Science Based Targets Network',
          label: <span className='whitespace-nowrap'>SBTN</span>,
        };
      case ComplianceFrameworks.gri:
        return {
          src: complianceGriSrc,
          alt: 'Global Reporting Initiative',
          label: <span className='whitespace-nowrap'>GRI</span>,
        };
      case ComplianceFrameworks.cdp:
        return {
          src: complianceCdpSrc,
          alt: 'Carbon Disclosure Project',
          label: <span className='whitespace-nowrap'>CDP</span>,
        };
      case ComplianceFrameworks.sdg:
        return {
          src: complianceSdgSrc,
          alt: 'Sustainable Development Goals',
          label: <span className='whitespace-nowrap'>SDG</span>,
        };
      case ComplianceFrameworks.iucn:
        return {
          src: complianceIucnSrc,
          alt: 'IUCN Red List',
          label: <span className='whitespace-nowrap'>IUCN</span>,
        };
      case ComplianceFrameworks.cbd:
        return {
          src: complianceCbdSrc,
          alt: 'Convention on Biological Diversity',
          label: <span className='whitespace-nowrap'>CBD</span>,
        };
      case ComplianceFrameworks.gbf:
        return {
          src: complianceGbfSrc,
          alt: 'Global Biodiversity Framework',
          label: <span className='whitespace-nowrap'>GBF</span>,
        };
      default:
        return exhaustivenessCheck(framework);
    }
  };

  const logoDetails = getLogoDetails(logo as ComplianceFrameworks);

  return (
    <Stack direction='row' spacing={1} className='items-center rounded-lg border border-divider px-3 py-2'>
      <Stack center className='aspect-square w-8'>
        <img src={logoDetails.src} alt={logoDetails.alt} className='w-full' />
      </Stack>
      <span className='typography-body2 flex max-w-min whitespace-break-spaces uppercase leading-none text-text-secondary'>
        {logoDetails.label}
      </span>
    </Stack>
  );
};

/**
 * Formats the latest date. Returns 'N/A' if an empty list is provided, or if
 * one of the dates is invalid.
 */
export const printLatestDate = (dates: unknown[]) => {
  return getLatestDate(dates.filter((d) => !!d))?.toLocaleDateString(i18next.language) ?? 'N/A';
};

export const printNthYearDate = (dates: unknown[], n: number) => {
  const latestDate = getLatestDate(dates.filter((d) => !!d));
  if (!latestDate) return 'N/A';

  const nthYearDate = new Date(latestDate);
  nthYearDate.setFullYear(nthYearDate.getFullYear() + n);
  return nthYearDate.toLocaleDateString(i18next.language);
};

export type ExplainerWellProps = {
  trendLabel?: TrendLabel | null;
  benchmark?: ConservationBenchmarkType | null;
  title?: React.ReactNode;
  body: React.ReactNode;
  variant?: 'default' | 'negative';
};

export const ConservationExplainerWell = ({ benchmark, title, body, trendLabel, variant }: ExplainerWellProps) => {
  return (
    <Stack spacing={4} className='rounded-2xl bg-neutral-black-4 px-4 py-6'>
      <Stack direction='row' spacing={4}>
        {benchmark && <BenchmarkPill benchmark={benchmark} />}
        {trendLabel && <TrendPill trendLabel={trendLabel} variant={variant} />}
      </Stack>
      <ExplainerWellBase title={title} body={body} />
    </Stack>
  );
};

export const ExplainerWell = ({ title, body }: ExplainerWellProps) => {
  return <ExplainerWellBase title={title} body={body} className='rounded-2xl bg-neutral-black-4 px-4 py-6' />;
};

const ExplainerWellBase = ({ title, body, className }: ExplainerWellProps & { className?: string }) => {
  return (
    <Stack spacing={4} className={className}>
      {title && <span className='typography-body1Semibold font-medium'>{title}</span>}
      <span className='leading-normal text-text-secondary [&_p]:mb-4'>{body}</span>
    </Stack>
  );
};

export type LatestAnalysisDatestampProps = (
  | {
      date: DateLike;
      facts?: never;
    }
  | {
      facts: FactLike | FactLike[];
      date?: never;
    }
) & { label?: React.ReactNode };

export const useLatestAnalysisDatestamp = (props: LatestAnalysisDatestampProps) => {
  const { t } = useTranslation();

  const label = props.label ?? t('shared.ncaDetail.details.labels.latestAnalysis');

  let date: Date | null = null;

  if ('date' in props) {
    const dateLike = props.date;
    date = dateLike ? new Date(dateLike) : null;
  } else if ('facts' in props) {
    if (Array.isArray(props.facts)) {
      date = getLatestDate(props.facts.map((f) => f.measured_at)) ?? null;
    } else {
      const timeString = props.facts?.measured_at;
      date = timeString ? new Date(timeString) : null;
    }
  }

  const datestamp: string = date?.toLocaleDateString() ?? 'N/A';

  return `${label} ${datestamp}`;
};
